import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

/**
 * Singleton service that wraps the Page Visibility API as a reactive stream.
 * Used to throttle polling when the browser tab is hidden, preventing excessive
 * API load from backgrounded sessions (see Jira: polling load reduction).
 *
 * The static `instance` property allows non-dependency injection classes (e.g. Application) to
 * access visibility state without constructor injection.
 */
@Injectable({
  providedIn: 'root',
})
export class PageVisibilityService implements OnDestroy {
  // Static reference for non-dependency injection consumers — set once in constructor.
  // Safe because providedIn:'root' guarantees a single instance.
  static instance: PageVisibilityService = null

  readonly visible$ = new BehaviorSubject<boolean>(document.visibilityState !== 'hidden')

  // Stored reference so we can removeEventListener in ngOnDestroy (needed for TestBed cleanup)
  private readonly onVisibilityChange = () => {
    this.visible$.next(document.visibilityState !== 'hidden')
  }

  get isVisible(): boolean {
    return this.visible$.value
  }

  constructor() {
    PageVisibilityService.instance = this
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  ngOnDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    if (PageVisibilityService.instance === this) {
      PageVisibilityService.instance = null
    }
  }
}
